import {Component} from '@angular/core';
import {SearchForm} from '../../../../sdk/component/search/searchForm';
import {Router} from '@angular/router';
import {AuthenticationService} from '../../../../sdk/service/authentication.service';
import {
    AccountsSortField,
    BankDetails,
    FileTransfer,
    SettlementBatch,
    SettlementDetails,
    SettlementSummary,
    VerificationStatus,
    WebBusinessService,
    WebSettlementService
} from 'web-angular-client';
import {SearchService} from '../../../../sdk/component/search/search.service';
import {ROUTES} from '../../../../app.routes';
import {saveAs} from 'file-saver';
import {AlertService} from '../../../../sdk/service/alert.service';
import {formatDate} from '@angular/common';
import {EnvironmentService} from '../../../../sdk/service/environment.service';
import {BatchStatusUtil} from '../../../../sdk/util/batchStatusUtil';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {finalize} from 'rxjs';

@Component({
    selector: 'app-batch-list',
    templateUrl: './batch-list.component.html',
    styleUrls: ['./batch-list.component.scss'],
    standalone: false
})
export class BatchListComponent extends SearchForm
{
    hideOutstandingAvsVerificationMessage: boolean = true;
    settlementDetails: SettlementDetails = null;
    settlementSummary: SettlementSummary = null;
    private readonly reconFileDownloadableDate: string;

    constructor(
        private router: Router,
        protected authenticationService: AuthenticationService,
        private webSettlementService: WebSettlementService,
        protected webBusinessService: WebBusinessService,
        protected searchService: SearchService,
        protected alertService: AlertService,
        private ngxLoader: NgxUiLoaderService,
        protected environmentService: EnvironmentService,
        protected batchStatusUtil: BatchStatusUtil)
    {
        super(authenticationService, webSettlementService, searchService, environmentService);
        this.reconFileDownloadableDate = formatDate(this.environmentService.reconFileDownloadable, 'yyyy-MM-dd', 'en');
        this.loadSettlementSummary();
        this.loadAvsStatus();
        this.loadSettlementDetails();
    }

    get contents(): SettlementBatch[]
    {
        return this.results?.content;
    }

    get hideSettlementDetails()
    {
        return !this.settlementDetails?.settlementFee && !this.settlementDetails?.minimumSettlementAmount;
    }

    getSettlementFeeInclVat(): number
    {
        if (!this.settlementDetails?.settlementFee)
        {
            return 0;
        }
        const vatMultiplier = 1 + this.settlementDetails.vat;
        return Math.round(this.settlementDetails.settlementFee * vatMultiplier);
    }

    view(batch: SettlementBatch)
    {
        this.router.navigate([ROUTES.SECURE.SETTLEMENT_BATCH.VIEW.pathSansParameters, batch.id]).then();
    }

    getDefaultSortField(): string
    {
        return AccountsSortField.ID;
    }

    getReconFileTooltipText()
    {
        return `Download a list of transactions that form part of a settlement batch. The file is in CSV format.`;
    }

    isReconFileDownloadable(batch: SettlementBatch): boolean
    {
        return batch.dateTimeClosed !== null;
    }

    getDisabledReconFileDownloadToolTip(): string
    {
        return `Only available for settlement batches created after ${this.reconFileDownloadableDate}.`;
    }

    getAccountReconFile(batch: SettlementBatch)
    {
        if (this.isGuestAdminUser())
        {
            this.alertService.error('Guest accounts cannot perform this action.', false);
            return;
        }

        if (!this.isReconFileDownloadable(batch))
        {
            const formattedDate = formatDate(this.environmentService.reconFileDownloadable, 'yyyy-MM-dd', 'en');
            this.alertService.error(`Unable to download recon files for legacy settlement batches created before ${formattedDate}.`);
            return;
        }

        this.ngxLoader.start();
        this.webSettlementService
            .getAccountReconFileForSettlementBatch(batch.id).pipe(finalize(() => this.ngxLoader.stop()))
            .subscribe({
                next: (fileTransfer: FileTransfer) =>
                {
                    if (null != fileTransfer.fileContent)
                    {
                        let contents = atob(fileTransfer.fileContent);
                        let blob = new Blob([contents], {type: 'text/csv;charset=utf-8'});
                        saveAs(blob, `${fileTransfer.fileName}.${fileTransfer.fileExtension}`);
                    }
                    else
                    {
                        this.alertService.info('Settlement batch recon file unavailable for download.');
                    }
                },
                error: error =>
                {
                    this.debugOutput(error);
                    this.alertService.error('Failed to retrieve recon file for the selected settlement batch. Please ' +
                        'try again later.');
                }
            });
    }

    public getBatchStatusUtil(): BatchStatusUtil
    {
        return this.batchStatusUtil;
    }

    public create()
    {
        // inherited abstract method that is unused
    }

    public getDate(): Date
    {
        return new Date();
    }

    private loadAvsStatus()
    {
        this.webBusinessService.getBankingDetails().subscribe({
            next: (bankDetails: BankDetails) =>
            {
                const isVerified: boolean = bankDetails != null && bankDetails.verificationStatus === VerificationStatus.VERIFIED;
                this.hideOutstandingAvsVerificationMessage = isVerified || this.isGuestAdminUser();
            }
        });
    }

    private loadSettlementDetails()
    {
        this.webSettlementService.getSettlementDetails().subscribe({
            next: (settlementDetails: SettlementDetails) =>
            {
                this.settlementDetails = settlementDetails;
            }
        });
    }

    private loadSettlementSummary()
    {
        this.webSettlementService.getSettledSettlementSummaryForTheMonth().subscribe({
            next: (settlementSummary: SettlementSummary) =>
            {
                this.settlementSummary = settlementSummary;
            }
        });
    }
}